import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { subscribeN4, when } from '../lib/n4';

interface Note { id: number; kind: string; title: string; body: string; link: string | null; project_id: number | null; seen: number; created_at: number }

// Fajtánként: hang, szín, felirat.
const KIND: Record<string, { sound: string; cls: string; label: string }> = {
  kesz: { sound: 'done', cls: 'text-emerald-300', label: 'kész' },
  valasz: { sound: 'reply', cls: 'text-sky-300', label: 'válasz' },
  munkas: { sound: 'worker', cls: 'text-emerald-300', label: 'munkás' },
  jatek: { sound: 'game', cls: 'text-[var(--color-accent)]', label: 'játék' },
  figyelem: { sound: 'alert', cls: 'text-amber-300', label: 'figyelem' },
  hiba: { sound: 'alert', cls: 'text-red-400', label: 'hiba' },
  hiany: { sound: 'alert', cls: 'text-amber-300', label: 'hiány' },
  javaslat: { sound: 'reply', cls: 'text-sky-300', label: 'javaslat' },
  uzenet: { sound: 'reply', cls: 'text-sky-300', label: 'üzenet' },
  chat: { sound: 'reply', cls: 'text-sky-300', label: 'chat' },
  kiadas: { sound: 'alert', cls: 'text-amber-300', label: 'kiadás' },
  proba: { sound: 'done', cls: 'text-[var(--color-accent)]', label: 'próbáld ki' },
};
const kindOf = (k: string) => KIND[k] ?? { sound: 'reply', cls: 'text-neutral-300', label: k };

// Beállítások a böngészőben (csak ennek a gépnek a kényelme).
const pref = (k: string, d: boolean) => { try { const v = localStorage.getItem(k); return v === null ? d : v === '1'; } catch { return d; } };
const setPref = (k: string, v: boolean) => { try { localStorage.setItem(k, v ? '1' : '0'); } catch { /* privát mód */ } };

// Értesítési központ: harang a fejlécben (olvasatlan szám), lenyíló lista, felugró jelzés, hang és asztali értesítés.
export function NotificationCenter() {
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState<Note[]>([]);
  const [unseen, setUnseen] = useState(0);
  const [open, setOpen] = useState(false);
  const [toasts, setToasts] = useState<Note[]>([]);
  const [sound, setSound] = useState(() => pref('cw_notify_sound', true));
  const [desktop, setDesktop] = useState(() => pref('cw_notify_desktop', false) && typeof Notification !== 'undefined' && Notification.permission === 'granted');
  const locRef = useRef(location.pathname + location.search); locRef.current = location.pathname + location.search;
  const soundRef = useRef(sound); soundRef.current = sound;
  const desktopRef = useRef(desktop); desktopRef.current = desktop;
  const boxRef = useRef<HTMLDivElement>(null);

  const load = useCallback(() => { void api<{ notifications: Note[]; unseen: number }>('/api/n4/notifications?limit=40').then((r) => { setItems(r.notifications); setUnseen(r.unseen); }).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  // Az olvasatlanok száma a lap címében is látszik (háttérben futó lapnál hasznos).
  useEffect(() => { const base = document.title.replace(/^\(\d+\)\s*/, ''); document.title = unseen ? `(${unseen}) ${base}` : base; }, [unseen]);

  const play = (kind: string) => { if (!soundRef.current) return; try { const a = new Audio(`/sounds/${kindOf(kind).sound}.mp3?v=2`); a.volume = 0.6; void a.play().catch(() => {}); } catch { /* nincs hang */ } };

  useEffect(() => subscribeN4((ev) => {
    if (ev.type !== 'notify') return;
    const n = { ...(ev as unknown as Note), seen: 0 };
    // Ha épp ennek a projektnek a terminálját nézed, a „válaszolt” jelzés fölösleges: látod.
    const here = n.project_id != null && document.visibilityState === 'visible' && document.hasFocus() && locRef.current.startsWith(`/projects/${n.project_id}`) && !/tab=(?!terminal)/.test(locRef.current);
    if (here && n.kind === 'valasz') { void api('/api/n4/notifications/seen', { method: 'POST', body: JSON.stringify({ id: n.id }) }); return; }
    setItems((prev) => [n, ...prev].slice(0, 40)); setUnseen((u) => u + 1);
    play(n.kind);
    setToasts((t) => [...t.slice(-2), n]);
    window.setTimeout(() => setToasts((t) => t.filter((x) => x.id !== n.id)), 7000);
    // Asztali értesítés, ha nem ezt az oldalt nézed: másik lap, kis méretre tett VAGY más ablak mögötti böngésző (Windowson ez utóbbi „látható”).
    if (desktopRef.current && (document.visibilityState !== 'visible' || !document.hasFocus()) && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      try { const dn = new Notification(n.title, { body: n.body, tag: `cw-${n.id}` }); dn.onclick = () => { window.focus(); if (n.link) navigate(n.link); dn.close(); }; } catch { /* nem támogatott */ }
    }
  }), [navigate]);

  // Kattintás a lenyílón kívül: bezár.
  useEffect(() => { if (!open) return undefined; const h = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false); }; document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h); }, [open]);

  const go = (n: Note) => {
    if (!n.seen) { void api('/api/n4/notifications/seen', { method: 'POST', body: JSON.stringify({ id: n.id }) }); setItems((p) => p.map((x) => (x.id === n.id ? { ...x, seen: 1 } : x))); setUnseen((u) => Math.max(0, u - 1)); }
    setToasts((t) => t.filter((x) => x.id !== n.id)); setOpen(false);
    if (n.link) navigate(n.link);
  };
  // A harang megnyitása = mindet láttad: a szám eltűnik. A lista ebben a nyitásban még mutatja, melyik volt új (pötty).
  const openBell = () => {
    if (open) { setOpen(false); setItems((p) => p.map((x) => ({ ...x, seen: 1 }))); return; }
    setOpen(true);
    if (!unseen) { load(); return; }
    // Előbb a friss lista (még a pöttyökkel), aztán az „olvasott” mentés — így a szám nem ugrik vissza.
    setUnseen(0); setToasts([]);
    void api<{ notifications: Note[] }>('/api/n4/notifications?limit=40').then((r) => setItems(r.notifications)).catch(() => {})
      .finally(() => { void api('/api/n4/notifications/seen', { method: 'POST' }).catch(() => {}); });
  };
  const allSeen = () => { void api('/api/n4/notifications/seen', { method: 'POST' }); setItems((p) => p.map((x) => ({ ...x, seen: 1 }))); setUnseen(0); };
  const toggleSound = () => { const v = !sound; setSound(v); setPref('cw_notify_sound', v); if (v) play('reply'); };
  const perm = typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;
  const testDesktop = () => {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') { alert('Előbb kapcsold be az asztali értesítést.'); return; }
    // Kis késleltetés, hogy legyen időd átváltani egy másik ablakra; a Windows Fókuszsegéd elnyelheti.
    window.setTimeout(() => { try { new Notification('AI Project Manager — próba', { body: 'Ha ezt látod, az asztali értesítés működik.', tag: 'cw-test' }); } catch { alert('A böngésző nem tudott értesítést megjeleníteni.'); } }, 3000);
  };
  const toggleDesktop = async () => {
    if (typeof Notification === 'undefined') { alert('Ez a böngésző nem támogatja az asztali értesítést.'); return; }
    if (desktop) { setDesktop(false); setPref('cw_notify_desktop', false); return; }
    const perm = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
    if (perm === 'granted') { setDesktop(true); setPref('cw_notify_desktop', true); } else alert('A böngésző nem engedélyezte az értesítést (a címsor melletti lakat-ikonnál engedélyezheted).');
  };

  return (
    <>
      <div ref={boxRef} className="relative">
        <button onClick={openBell} title="Értesítések" className={`a-bell ${unseen ? 'has' : ''}`}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></svg>
          {unseen > 0 && <span className="bd">{unseen > 99 ? '99+' : unseen}</span>}
        </button>
        {open && (
          <div className="a-bellpop fixed left-3 right-3 top-14 z-50 sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[400px]">
            <div className="a-bp-head">
              <h3>Értesítések</h3>
              <button className="a-btn sm ghost" disabled={!unseen} onClick={allSeen}>Mind olvasott</button>
            </div>
            <div className="a-bp-list">
              {items.length === 0 && <p className="a-bp-empty">Még nincs értesítés. Szólok, ha egy ügynök végzett vagy válaszolt, ha egy munkás kész lett vagy elakadt, és ha új játék-build érkezett.</p>}
              {items.map((n, i) => (
                <div role="button" tabIndex={0} key={n.id} onClick={() => go(n)} onKeyDown={(e) => { if (e.key === 'Enter') go(n); }} className={`a-note k-${n.kind} ${n.seen ? '' : 'unread'}`} style={{ ['--i' as string]: Math.min(i, 10) }}>
                  <i className="sq" aria-hidden="true" />
                  <div className="min-w-0">
                    <div className="nh"><b>{n.title}</b><span className="kind">{kindOf(n.kind).label}</span></div>
                    {n.body && <p>{n.body}</p>}
                    <time>{when(n.created_at)}</time>
                  </div>
                </div>
              ))}
            </div>
            <div className="a-bp-foot">
              <label className="a-tg"><input type="checkbox" checked={sound} onChange={toggleSound} /><span aria-hidden="true" />Hang</label>
              <label className="a-tg" title="Akkor szól, ha nem ezt az oldalt nézed"><input type="checkbox" checked={desktop} onChange={() => void toggleDesktop()} /><span aria-hidden="true" />Asztali értesítés</label>
              {desktop && <button className="a-btn sm" onClick={testDesktop} title="3 mp múlva küld egy próbát: válts át másik ablakra">Próba</button>}
              {!desktop && perm !== 'denied' && perm !== 'unsupported' && <button className="a-btn sm pri a-bp-wide" onClick={() => void toggleDesktop()}>Asztali értesítés bekapcsolása ezen a gépen</button>}
              {perm === 'denied' && <p className="a-bp-warn">A böngésző tiltja az értesítést ennél az oldalnál. Kattints a címsor melletti lakat-ikonra, ott Értesítések → Engedélyezés, majd töltsd újra az oldalt.</p>}
              {desktop && <p className="a-bp-hint">Windowson ha nem jelenik meg: Gépház → Rendszer → Értesítések. A böngésző legyen engedélyezve, a Fókuszsegéd legyen kikapcsolva.</p>}
            </div>
          </div>
        )}
      </div>
      {/* Felugró jelzések a jobb alsó sarokban */}
      <div className="pointer-events-none fixed bottom-3 right-3 z-50 flex w-[min(380px,calc(100vw-24px))] flex-col gap-2">
        {toasts.map((n) => (
          <div role="button" tabIndex={0} key={n.id} onClick={() => go(n)} className={`a-toast k-${n.kind} pointer-events-auto`}>
            <i className="sq" aria-hidden="true" />
            <div className="min-w-0">
              <b>{n.title}</b><span className="kind">{kindOf(n.kind).label}</span>
              {n.body && <p>{n.body}</p>}
            </div>
            <button className="x" title="Bezárás" onClick={(e) => { e.stopPropagation(); setToasts((t) => t.filter((x) => x.id !== n.id)); }}>×</button>
          </div>
        ))}
      </div>
    </>
  );
}
